import { create } from 'zustand';
import { User, DashboardStats, Language, PlatformConfig } from '../types';
import { authService } from '../services/authService';
import { languageService } from '../services/languageService';
import { platformConfigService } from '../services/platformConfigService';
import { apiService } from '../services/api';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
}

interface DashboardState {
  stats: DashboardStats | null;
  isLoadingStats: boolean;
}

interface AppState {
  languages: Language[];
  platformConfig: PlatformConfig | null;
  isLoadingConfig: boolean;
}

interface AppStore extends AuthState, DashboardState, AppState {
  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
  
  // Dashboard actions
  loadDashboardStats: () => Promise<void>;
  
  // App actions
  loadLanguages: () => Promise<void>;
  loadPlatformConfig: () => Promise<void>;
  updatePlatformConfig: (config: Partial<PlatformConfig>) => Promise<void>;
}

export const useStore = create<AppStore>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true,
  error: null,
  stats: null,
  isLoadingStats: false,
  languages: [],
  platformConfig: null,
  isLoadingConfig: false,

  // Auth actions
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login({ email, password });
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
    } catch (error: any) {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error.message || 'Login failed'
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        stats: null,
        languages: [],
        platformConfig: null
      });
    }
  },

  initializeAuth: async () => {
    set({ isInitializing: true, error: null });
    try {
      await authService.initialize();
      const user = authService.getCurrentUser();
      if (user) {
        set({ user, isAuthenticated: true, isInitializing: false });
      } else {
        set({ user: null, isAuthenticated: false, isInitializing: false });
      }
    } catch (error: any) {
      console.warn('Auth initialization failed:', error);
      set({ 
        user: null, 
        isAuthenticated: false, 
        isInitializing: false,
        error: null // Don't show error to user for initialization failures
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  // Dashboard actions — Snapshot home dashboard admin.
  // BE: GET /api/admin/dashboard/stats → DashboardOverviewDto
  // (totale/attivi utenti + utenti per ruolo + monthly registrations +
  // enabled languages + translation progress).
  // Le metriche subscription/revenue arrivano da endpoint paralleli, non da qui.
  loadDashboardStats: async () => {
    set({ isLoadingStats: true });
    try {
      const response = await apiService.get<DashboardStats>('/api/admin/dashboard/stats');
      set({ stats: response.payload, isLoadingStats: false });
    } catch (error: any) {
      console.error('Error loading dashboard stats:', error);
      // Fallback: stato vuoto a zero invece di crash UI. Il chiamante
      // (Dashboard.tsx) gestisce stats?.totalUsers || 0, quindi va bene null.
      set({ stats: null, isLoadingStats: false });
    }
  },

  // App actions
  loadLanguages: async () => {
    try {
      const languages = await languageService.getAllLanguages();
      set({ languages });
    } catch (error: any) {
      console.error('Error loading languages:', error);
      set({ languages: [] });
    }
  },

  loadPlatformConfig: async () => {
    set({ isLoadingConfig: true });
    try {
      const config = await platformConfigService.getPlatformConfig();
      set({ platformConfig: config, isLoadingConfig: false });
    } catch (error: any) {
      console.error('Error loading platform config:', error);
      set({ isLoadingConfig: false });
    }
  },

  updatePlatformConfig: async (configUpdate: Partial<PlatformConfig>) => {
    set({ isLoadingConfig: true });
    try {
      const updated = await platformConfigService.updatePlatformConfig(configUpdate as PlatformConfig);
      set({ platformConfig: updated, isLoadingConfig: false });
    } catch (error: any) {
      console.error('Error updating platform config:', error);
      set({ isLoadingConfig: false });
      throw error;
    }
  }
}));

export default useStore;